import { faRepeat } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import Select from 'react-select';
import { Badge, Button, Col, Collapse, Input, Label, ListGroup, ListGroupItem, Row, Spinner } from 'reactstrap';
import Swal from 'sweetalert2';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
    downloadDocumentToBrowser, getExistingTemplates, getTemplateByDescription, migrateExistingTemplates,
    uploadTemplateFile, deleteExistingTemplate, getBackedUpTemplates
} from '../../redux/DocumentsSlice';
import { getReportYears, selectAvailableReportYears } from '../../redux/ReportSlice';
import { TaxonomyTypeEnum } from "../../redux/TaxonomyVersionSlice";
import { GenericActionTile } from '../core/GenericActionTile';
import { GenericFileUploader } from '../core/GenericFileUploader';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';


interface fileData {
    fileName: string;
    fileType: "DATA_COLLECTION" | "REPORT" | "TAXONOMY" | "UNKNOWN";
}

const TemplateMaintenance = () => {

    const dispatch = useAppDispatch();

    //selectors

    const availableReportYears = useAppSelector(selectAvailableReportYears);

    //local hooks

    const defaultValue = { label: 'UNKNOWN', value: 'UNKNOWN' };
    const defaultYearSelection = { label: 'Select A Year', value: 'UNKNOWN' };

    const [workingTemplateList, setWorkingTemplateList] = React.useState<Array<fileData>>([]);
    const [adjustingTemplateList, setAdjustingTemplateList] = React.useState<Array<fileData>>([]);
    const [templateFileList, setTemplateFileList] = React.useState<Array<{ value: string; label: string; }>>([]);
    const [file, setFile] = React.useState<File | undefined>(undefined);
    const [fileDescription, setFileDescription] = React.useState<string>("");
    const [isAllOnRightSide, setIsAllOnRightSide] = React.useState<boolean>(false);
    const [componentIsLoading, setComponentIsLoading] = React.useState<boolean>(false);
    const [versioning, setVersioning] = React.useState<string>("");
    const [reportName, setReportName] = React.useState<string>("");
    const [isByFund, setIsByFund] = React.useState<boolean>(false);
    const [isIXBRL, setIsIXBRL] = React.useState<boolean>(false);

   

    const [selectedTemplateType, setSelectedTemplateType] = React.useState<{ value: string; label: string; }>(defaultValue);
    const [selectedTaxonomyType, setSelectedTaxonomyType] = React.useState<{ value: string; label: string; }>(defaultValue);
    const [selectedOptionalTaxonomyType, setSelectedOptionalTaxonomyType] = React.useState<{ value: string; label: string; }>(defaultValue);
    const [selectedAfrYear, setSelectedAfrYear] = React.useState<{ value: string; label: string; }>(defaultYearSelection);
    const [selectedAfrYearTwo, setSelectedAfrYearTwo] = React.useState<{ value: string; label: string; }>(defaultYearSelection);
    const [selectedEntityType, setSelectedEntityType] = React.useState<{ value: string; label: string; }>(defaultValue);
    const [selectedEntityTypeTwo, setSelectedEntityTypeTwo] = React.useState<{ value: string; label: string; }>(defaultValue);
    const [selected939QuarterType, setSelected939QuarterType] = React.useState<{ value: string; label: string; }>(defaultValue);
    const [selectedInlineViewerType, setSelectedInlineViewerType] = React.useState<{ value: string; label: string; }>(defaultValue);
    const [selectedUpdateFileName, setSelectedUpdateFileName] = React.useState<{ value: string; label: string; }>({label: "Select a template", value:"UNKNOWN"});


    const [reportYearOptions, setReportYearOptions] = React.useState<Array<{ value: string; label: string; }>>([]);
    const [reportYearOptionsTwo, setReportYearOptionsTwo] = React.useState<Array<{ value: string; label: string; }>>([]);

    const [renderMode, setRenderMode] = React.useState<'Create' | 'Browse' | 'Update' | 'Migrate'>('Create');

    const templateTypeList: Array<{ value: string; label: string; }> = [
        { label: 'Data Collection', value: 'DATA_COLLECTION' },
        { label: 'Report', value: 'REPORT' },
        { label: 'UNKNOWN', value: 'UNKNOWN'}];

    const taxonomyTypeList: Array<{ value: string; label: string; }> = [
        { label: 'AFR', value: 'AFR' },
        { label: 'Court Reporting 939', value: 'COURT_REPORTING_939' },
        { label: 'Court Reporting 29', value: 'COURT_REPORTING_29' },
        { label: 'UNKNOWN', value: 'UNKNOWN' }];

    const taxonomyTypeListOptional: Array<{ value: string; label: string; }> = [
        { label: 'AFR', value: 'AFR' },
        { label: 'Court Reporting 939', value: 'COURT_REPORTING_939' },
        { label: 'Court Reporting 29', value: 'COURT_REPORTING_29' },
        { label: 'Not Specified', value: 'N/A' },
        { label: 'UNKNOWN', value: 'UNKNOWN' }];

    const inlineViewerTypeList: Array<{ value: string; label: string; }> = [
        { label: 'Excel', value: 'EXCEL' },
        { label: 'XHTML', value: 'XHTML' },
        { label: 'PDF', value: 'PDF' },
        { label: 'UNKNOWN', value: 'UNKNOWN' }];


        //TODO add ByFund option for AFR and Mapping to court reporting

    const entityCollectionTemplateTypes: Array<{ value: string; label: string; }> = [
        { label: 'City', value: 'CI' },
        { label: 'County', value: 'CO' },
        { label: 'Other Entity', value: 'OE' },
        { label: 'Special District', value: 'SD' },
        { label: 'Dependent Special District', value: 'SDD' },
        { label: 'Not Specified', value: 'N/A' },
        { label: 'UNKNOWN', value: 'UNKNOWN' }];

    const entityReportTemplateTypes: Array<{ value: string; label: string; }> = [
        { label: 'City', value: 'CI' },
        { label: 'County', value: 'CO' },
        { label: 'Other Entity', value: 'OE' },
        { label: 'Special District', value: 'SD' },
        { label: 'Dependent Special District', value: 'SDD' },
        { label: 'Not Specified', value: 'N/A' },
        { label: 'UNKNOWN', value: 'UNKNOWN' }];


    const courtReporting939QuarterList: Array<{ value: string; label: string; }> = [
        { label: 'Quarter 1', value: 'Q1' },
        { label: 'Quarter 2', value: 'Q2' },
        { label: 'Quarter 3', value: 'Q3' },
        { label: 'Quarter 4', value: 'Q4' },
        { label: 'Not Specified', value: 'N/A' },
        { label: 'UNKNOWN', value: 'UNKNOWN' }];


    //functions

    function MoveToWorkingList(option: fileData) {
        let temporaryWorkingList: Array<fileData> = [];
        temporaryWorkingList = temporaryWorkingList.concat(workingTemplateList);

        let temporaryAdjustingList: Array<fileData> = [];
        temporaryAdjustingList = temporaryAdjustingList.concat(adjustingTemplateList);

        let elementIndex = temporaryAdjustingList.indexOf(option);
        if (elementIndex > -1) {
            temporaryAdjustingList.splice(elementIndex, 1);
        }
        temporaryWorkingList.push(option);
        setWorkingTemplateList(temporaryWorkingList);

        setAdjustingTemplateList(temporaryAdjustingList);
    }

    function MoveToAdjustingList(option: fileData) {
        let temporaryWorkingList: Array<fileData> = [];
        temporaryWorkingList = temporaryWorkingList.concat(workingTemplateList);

        let temporaryAdjustingList: Array<fileData> = [];
        temporaryAdjustingList = temporaryAdjustingList.concat(adjustingTemplateList);

        let elementIndex = temporaryWorkingList.indexOf(option);
        if (elementIndex > -1) {
            temporaryWorkingList.splice(elementIndex, 1);
        }
        temporaryAdjustingList.push(option);
        setAdjustingTemplateList(temporaryAdjustingList);


        setWorkingTemplateList(temporaryWorkingList);      
    }

    function MoveAllToSameSide() {

        let allFiles: Array<fileData> = workingTemplateList.concat(adjustingTemplateList);

        if (isAllOnRightSide == true) {
            setWorkingTemplateList(allFiles);
            setAdjustingTemplateList([]);
        } else {
            setAdjustingTemplateList(allFiles);
            setWorkingTemplateList([]);
        }
        setIsAllOnRightSide(!isAllOnRightSide);
    }

    function handleTemplateTypeSelectionChange(target: any) {
        if (target) {

            //cleanup inputs
            clearDataCollectionInputs();
            clearReportInputs();
           
            //update input
            setSelectedTemplateType(target);
        }
    }

    function clearDataCollectionInputs() {
        setSelectedTaxonomyType(defaultValue);
        setSelectedAfrYear(defaultYearSelection);
        setSelectedEntityType(defaultValue);
        setSelected939QuarterType(defaultValue);
        setVersioning('');
    }

    function clearReportInputs() {
        setSelectedOptionalTaxonomyType(defaultValue);
        setSelectedAfrYear(defaultYearSelection);
        setSelectedEntityType(defaultValue);
        setSelectedInlineViewerType(defaultValue);
        setReportName('');
    }

    function handleCollectionTaxonomyTypeSelectionChange(target: any) {
        if (target) {

            //cleanup inputs
            setSelectedAfrYear(defaultYearSelection);
            setSelectedEntityType(defaultValue);
            setSelected939QuarterType(defaultValue);
            setVersioning('');

            //update input
            setSelectedTaxonomyType(target);
        }
    }

    function handleReportOptionalTaxonomyTypeSelectionChange(target: any) {
        if (target) {

            //cleanup inputs
            setSelectedAfrYearTwo(defaultYearSelection);
            setSelectedEntityTypeTwo(defaultValue);
            setSelectedInlineViewerType(defaultValue);
            setReportName('');

            //update input
            setSelectedOptionalTaxonomyType(target);
        }
    }

    function handleAfrYearSelectionChange(target: any) {
        if (target) {

            //cleanup inputs
            setSelectedEntityType(defaultValue);

            //update input
            setSelectedAfrYear(target);
        }
    }

    function handleAfrYearSelectionChangeTwo(target: any) {
        if (target) {

            //cleanup inputs
            setSelectedEntityTypeTwo(defaultValue);
            setSelectedInlineViewerType(defaultValue);

            //update input
            setSelectedAfrYearTwo(target);
        }
    }

    function handleEntityTypeSelectionChange(target: any) {
        if (target) {
                    
            //update input
            setSelectedEntityType(target);
        }
    }

    function handleEntityTypeSelectionChangeTwo(target: any) {
        if (target) {

            //cleanup inputs
            setSelectedInlineViewerType(defaultValue);

            //update input
            setSelectedEntityTypeTwo(target);
        }
    }

    function handle939QuarterTypeSelectionChange(target: any) {
        if (target) {

            //cleanup inputs
            setVersioning('');
           
            //update input
            setSelected939QuarterType(target);
        }
    }

    function handleInlineViewerChange(target: any) {
        if (target) {
            setSelectedInlineViewerType(target);
        }
    }

    function handleVersioningChange(value: string) {
        //TODO create lookup of past version to suggest sequential data or 
        if (value) {

            setVersioning(value);
        }
    }

    function handleReportNameChange(value: string) {
        if (value) {
            setReportName(value);
        }
    }

    React.useEffect(() => {

        let fileTemplateNameValue: string = "";

        if (selectedTemplateType.value == 'DATA_COLLECTION') {

            fileTemplateNameValue = 'DataCollectionTemplate';

            if ((selectedTaxonomyType.value == 'AFR')
                && (selectedAfrYear.value != 'UNKNOWN')) {
                fileTemplateNameValue = fileTemplateNameValue + selectedAfrYear.value;
            }

            if ((selectedTaxonomyType.value == 'AFR')
                && (selectedAfrYear.value != 'UNKNOWN')
                && (selectedEntityType.value != 'N/A' && selectedEntityType.value != 'UNKNOWN')) {
                fileTemplateNameValue = fileTemplateNameValue + "_" + selectedEntityType.value;
            }

            if ((selectedTaxonomyType.value == 'COURT_REPORTING_939' || selectedTaxonomyType.value == 'COURT_REPORTING_29')) {
                fileTemplateNameValue = fileTemplateNameValue + "_" + selectedTaxonomyType.value;
            }

            if ((selectedTaxonomyType.value == 'COURT_REPORTING_939')
                && (selected939QuarterType.value != 'N/A' && selected939QuarterType.value != 'UNKNOWN')) {
                fileTemplateNameValue = fileTemplateNameValue + "_" + selected939QuarterType.value;
            }

            if ((selectedTaxonomyType.value == 'COURT_REPORTING_939' || selectedTaxonomyType.value == 'COURT_REPORTING_29')
                && versioning.length > 0) {
                fileTemplateNameValue = fileTemplateNameValue + "_" + versioning;
            }


        } else if (selectedTemplateType.value == 'REPORT') {

            if (selectedOptionalTaxonomyType.value == 'AFR') {

                fileTemplateNameValue = fileTemplateNameValue + "AFRTemplate"; 

                if (selectedAfrYearTwo.value != 'UNKNOWN') {
                    fileTemplateNameValue = fileTemplateNameValue + selectedAfrYearTwo.value;
                }

                if ((selectedAfrYearTwo.value != 'UNKNOWN')
                    && (selectedEntityTypeTwo.value != 'N/A' && selectedEntityTypeTwo.value != 'UNKNOWN')) {
                    fileTemplateNameValue = fileTemplateNameValue + "_" + selectedEntityTypeTwo.value;
                }
            }

            if (selectedOptionalTaxonomyType.value == 'COURT_REPORTING_939' || selectedOptionalTaxonomyType.value == 'COURT_REPORTING_29') {
                fileTemplateNameValue = fileTemplateNameValue + selectedOptionalTaxonomyType.value + "Template";
                if (isIXBRL == true) {
                    fileTemplateNameValue = fileTemplateNameValue + "_IXBRL"
                }
            }

            if (selectedOptionalTaxonomyType.value == 'N/A') {
                fileTemplateNameValue = fileTemplateNameValue + reportName;
            }

            if (selectedInlineViewerType.value != 'UNKNOWN') {
                if (selectedInlineViewerType.value == 'XHTML') {
                    fileTemplateNameValue = fileTemplateNameValue + "-InlineViewer";
                }
                else if (selectedInlineViewerType.value == 'PDF') {
                    //TODO address dew details being different in naming conveition
                    fileTemplateNameValue = fileTemplateNameValue + "_PDF-InlineViewer";
                }
            }
            
        }

       
        setFileDescription(fileTemplateNameValue);

    }, [handleTemplateTypeSelectionChange, handleCollectionTaxonomyTypeSelectionChange, handleAfrYearSelectionChange,
        handleEntityTypeSelectionChange, handle939QuarterTypeSelectionChange, handleVersioningChange, handleReportOptionalTaxonomyTypeSelectionChange,
        handleInlineViewerChange, handleReportNameChange, handleEntityTypeSelectionChangeTwo
    ])

    function isCheckedTemplateType(value: string): boolean {
        let result: boolean = false;

        if (value && selectedTemplateType) {
            if (value == selectedTemplateType.value) {
                result = true;
            }
        }

        return result;
    }

    function isCheckedTaxonomyType(value: string): boolean {
        let result: boolean = false;

        if (value && selectedTaxonomyType) {
            if (value == selectedTaxonomyType.value) {
                result = true;
            }
        }

        return result;
    }

    function isCheckedOptionalTaxonomyType(value: string): boolean {
        let result: boolean = false;

        if (value && selectedOptionalTaxonomyType) {
            if (value == selectedOptionalTaxonomyType.value) {
                result = true;
            }
        }

        return result;
    }


    function isChecked939QuarterType(value: string): boolean {
        let result: boolean = false;

        if (value && selected939QuarterType) {
            if (value == selected939QuarterType.value) {
                result = true;
            }
        }

        return result;
    }

    function isCheckedInlineViewerType(value: string): boolean {
        let result: boolean = false;

        if (value && selectedInlineViewerType) {
            if (value == selectedInlineViewerType.value) {
                result = true;
            }
        }

        return result;
    }

    function handleRestoreClick(): void {

        let templateNameValues: Array<string> = [];

        for (var i = 0; i < adjustingTemplateList.length; i++) {
            templateNameValues.push(adjustingTemplateList[i].fileName);
        }

        dispatch(migrateExistingTemplates(templateNameValues))
            .then((result: any) => {
                if (result.payload != null) {
                    Swal.fire(result.payload, '', 'info');

                    setWorkingTemplateList([]);
                    setAdjustingTemplateList([]);
                    getexistingTemplatesForWorkingList();
                    setIsAllOnRightSide(false);
                }
                else {
                    Swal.fire('Oops...', 'Something went wrong!', 'error')
                }
            });


    }
    function getTemplateDisplayName(option: fileData): string {
        let result: string = option.fileName;

        if (option.fileName.includes("template;") == true) {
            result = option.fileName.replace("template;", "");
        }

        return result;
    }

    function addBadgeForBackedTemplates(option: fileData): React.ReactElement<any, string | React.JSXElementConstructor<any>> {

        let result: React.ReactElement<any, string | React.JSXElementConstructor<any>> = (<></>);

        if (option.fileName.includes("template;")) {
            result = (<Badge> Restored</Badge>);
        }

        return result;
    }

    function isMinimumViableSelected(): boolean | undefined {
        let result: boolean = false;

        if (allDataCollectionAfrInputsSelected()
            || allDataCollectionCourtReportingInputsSelected()
            || selectedInlineViewerType.value != 'UNKNOWN') {
            result = true;
        }
        

        return result;
    }

    function allDataCollectionAfrInputsSelected() {
        return selectedTemplateType.value == 'DATA_COLLECTION'
            && selectedTaxonomyType.value == 'AFR'
            && selectedAfrYear.value != 'UNKNOWN'
            && selectedEntityType.value != 'UNKNOWN';
    }

    function allReportAfrInputsSelected() {
        return selectedTemplateType.value == 'REPORT'
            && selectedOptionalTaxonomyType.value == 'AFR'
            && selectedAfrYearTwo.value != 'UNKNOWN'
            && selectedEntityTypeTwo.value != 'UNKNOWN';
    }

    function allDataCollectionCourtReportingInputsSelected() {
        return selectedTemplateType.value == 'DATA_COLLECTION'
                && ((selectedTaxonomyType.value == 'COURT_REPORTING_939' || selected939QuarterType.value != 'UNKNOWN')
            || (selectedTaxonomyType.value == 'COURT_REPORTING_29'))
    }

    function allReportCourtReportingInputsSelected() {
        return selectedTemplateType.value == 'REPORT'
            && ((selectedOptionalTaxonomyType.value == 'COURT_REPORTING_939' || selected939QuarterType.value != 'UNKNOWN')
            || (selectedOptionalTaxonomyType.value == 'COURT_REPORTING_29'))
    }


    const getexistingTemplatesForWorkingList = async () => {

        let temporaryLocalList: Array<fileData> = [];

        var resultAction = await dispatch(getExistingTemplates());
        var originalPromiseResult = unwrapResult(resultAction);

        if (originalPromiseResult) {
            for (var i = 0; i < originalPromiseResult.length; i++) {

                temporaryLocalList.push({ fileName: originalPromiseResult[i], fileType: 'UNKNOWN' })
            }
        }

        setWorkingTemplateList(temporaryLocalList);
    }

    const handleUpdateFileUpload = async function (actionType: string, entityType: string, entityId: string, uploadedFile: File | undefined) {
        if (uploadedFile !== undefined) {

            setComponentIsLoading(true);

            await uploadTemplateFile(uploadedFile, selectedUpdateFileName.value, dispatch).then((result: any) => {
                if (result != null) {
                    Swal.fire('', "Update to " + result.replace("template;", "") + " complete", 'success');                  
                }
                else {
                    Swal.fire('Oops...', 'Something went wrong!', 'error')
                }
            });

            refreshTemplateListForUpdate()
            setComponentIsLoading(false);

        }
    }

    const handleNewFileUpload = async function (actionType: string, entityType: string, entityId: string, uploadedFile: File | undefined) {
        if (uploadedFile !== undefined) {

            setComponentIsLoading(true);

            await uploadTemplateFile(uploadedFile, "template;" + fileDescription, dispatch).then((result: any) => {
                if (result != null) {
                    Swal.fire('', "Update to " + result.replace("template;", "") + " complete", 'success');
                }
                else {
                    Swal.fire('Oops...', 'Something went wrong!', 'error')
                }
            });

            setComponentIsLoading(false);
        }
    }

    async function handleTemplateDownload(atype: string, ename: string, ecode: string, aYear: number): Promise<void> {
        if (selectedUpdateFileName && selectedUpdateFileName.value) {
           
            const response = await dispatch(getTemplateByDescription(selectedUpdateFileName.value));
            const doc = unwrapResult(response);
            if (doc !== null) {
                await downloadDocumentToBrowser(doc);
            }
        }      
    }

    async function refreshTemplateListForUpdate() {



        let temporaryLocalList: Array<fileData> = [];

        var resultAction = await dispatch(getExistingTemplates());
        var originalPromiseResult = unwrapResult(resultAction);

        if (originalPromiseResult) {
            for (var i = 0; i < originalPromiseResult.length; i++) {

                temporaryLocalList.push({ fileName: originalPromiseResult[i], fileType: 'UNKNOWN' })
            }
        }

        setWorkingTemplateList(temporaryLocalList);


        var resultAction = await dispatch(getBackedUpTemplates());
        var originalPromiseResult = unwrapResult(resultAction);

        if (originalPromiseResult) {

            let temporaryLocalList: Array<fileData> = [];

            for (var i = 0; i < originalPromiseResult.length; i++) {

                temporaryLocalList.push({ fileName: originalPromiseResult[i], fileType: 'UNKNOWN' })
            }

            let tempList: Array<{ label: string; value: string; }> = [];

            for (var i = 0; i < temporaryLocalList.length; i++) {

                tempList.push({ label: getTemplateDisplayName(temporaryLocalList[i]), value: temporaryLocalList[i].fileName });
            }

            setTemplateFileList(tempList);
        }        
    }

   

    async function handleDeleteSelectedTemplate(): Promise<void> {
        setComponentIsLoading(true);
        const response = await dispatch(deleteExistingTemplate(selectedUpdateFileName.value)).then((result: any) => {
            if (result != null) {
                Swal.fire('', "Template deleted", 'success');                        
            }
            else {
                Swal.fire('Oops...', 'Something went wrong!', 'error')
            }
        });
        setSelectedUpdateFileName({ label: "Select a template", value: "UNKNOWN" });  
        refreshTemplateListForUpdate()

        setComponentIsLoading(false);
    }

    //use effects
    React.useEffect(() => {
 
        getexistingTemplatesForWorkingList();

        if (!(availableReportYears) || availableReportYears.length == 0) {
            dispatch(getReportYears(TaxonomyTypeEnum.AFR));
        }

    }, [])

    React.useEffect(() => {

        if (availableReportYears && availableReportYears.length > 0) {

            let afrYearListOptions: Array<{ value: string; label: string; }> = [];

            for (var i = 0; i < availableReportYears.length; i++) {
                afrYearListOptions.push({ label: availableReportYears[i].toString(), value: availableReportYears[i].toString() });
            }

            afrYearListOptions.sort((a, b) => { return a.value.localeCompare(b.value, undefined, { numeric: true }) });
            afrYearListOptions.reverse();         
            afrYearListOptions.push({ label: "Select A Year", value: "" });

            setReportYearOptions(afrYearListOptions);
            setReportYearOptionsTwo(afrYearListOptions);

            handleAfrYearSelectionChange({ label: "Select A Year", value: "" });
            handleAfrYearSelectionChangeTwo({ label: "Select A Year", value: "" });
        }

    }, [availableReportYears])

    React.useEffect(() => {

        if (renderMode) {
            if (renderMode == 'Update') {
                refreshTemplateListForUpdate();
            }
        }


    }, [renderMode])

    const createRender = (<React.Fragment>
        
        <Row>
            <Col>
                <h2>Create</h2>
            </Col>
        </Row>
        <Row className="mt-3">
            <Col>
                <Label>Create templates for reports or data collection</Label>
            </Col>
        </Row>
        <Row className="mt-4">
            <Col md="2">
                <Label>Template Type:</Label>
            </Col>
            <Col md="10">            
                <ul style={{listStyle:"none"} }>
                    <Row>
                        {templateTypeList.map(({ value, label }) => (
                            <Col md="2" hidden={value == 'UNKNOWN'} className="m-0 p-0" >
                                <li key={value}  >
                                    <Label>
                                        <Input
                                            type="radio"
                                            value={value}
                                            name={label}
                                            onChange={(e: any) => { handleTemplateTypeSelectionChange(e.target) }}

                                            checked={isCheckedTemplateType(value)} />

                                        {label}
                                    </Label>
                                </li>
                            </Col>                          
                        ))}
                    </Row>                   
                </ul>
            </Col>
        </Row>

        <Collapse isOpen={selectedTemplateType.value == 'DATA_COLLECTION' }>
            <Row className="mt-4">
                <Col md="2">
                    <Label>Taxonomy Type:</Label>
                </Col>
                <Col md="10">
                    <ul style={{ listStyle: "none" }}>
                        <Row>
                            {taxonomyTypeList.map(({ value, label }) => (
                                <Col md="2" hidden={value == 'UNKNOWN'} className="m-0 p-0" >
                                    <li key={value}  >
                                        <Label>
                                            <Input
                                                type="radio"
                                                value={value}
                                                name={label}
                                                onChange={(e: any) => { handleCollectionTaxonomyTypeSelectionChange(e.target) }}

                                                checked={isCheckedTaxonomyType(value)} />

                                            {label}
                                        </Label>
                                    </li>
                                </Col>
                            ))}
                        </Row>
                    </ul>
                </Col>
            </Row>
        </Collapse>

        <Collapse isOpen={selectedTemplateType.value == 'REPORT'}>
            <Row className="mt-4">
                <Col md="2">
                    <Label>Taxonomy Type:</Label>
                </Col>
                <Col md="10">
                    <ul style={{ listStyle: "none" }}>
                        <Row>
                            {taxonomyTypeListOptional.map(({ value, label }) => (
                                <Col md="2" hidden={value == 'UNKNOWN'} className="m-0 p-0" >
                                    <li key={value}  >
                                        <Label>
                                            <Input
                                                type="radio"
                                                value={value}
                                                name={label}
                                                onChange={(e: any) => { handleReportOptionalTaxonomyTypeSelectionChange(e.target) }}

                                                checked={isCheckedOptionalTaxonomyType(value)} />

                                            {label}
                                        </Label>
                                    </li>
                                </Col>
                            ))}
                        </Row>
                    </ul>
                </Col>
            </Row>
        </Collapse>

        <Collapse isOpen={selectedTaxonomyType.value == 'AFR' && selectedTemplateType.value == 'DATA_COLLECTION'}>
            <Row className="mt-4">
                <Col md="2">
                    <Label>Afr Year:</Label>
                </Col>
                <Col md="2">
                    <Select
                        options={reportYearOptions}
                        value={selectedAfrYear}
                        onChange={(e: { value: string, label: string }) => { handleAfrYearSelectionChange(e); }}

                    />
                </Col>
            </Row>
            <Row className="mt-4">
                <Col md="2">
                    <Label>Entity Type:</Label>
                </Col>
                <Col md="10">
                    <ul style={{ listStyle: "none" }}>
                        <Row>
                            {entityReportTemplateTypes.map(({ value, label }) => (
                                <Col md="2" hidden={value == 'UNKNOWN'} className="m-0 p-0" >
                                    <li key={value}  >
                                        <Label>
                                            <Input
                                                type="radio"
                                                value={value}
                                                name={label}
                                                onChange={(e: any) => { handleEntityTypeSelectionChange(e.target) }}

                                                checked={selectedEntityType.value == value} />

                                            {label}
                                        </Label>
                                    </li>
                                </Col>
                            ))}
                        </Row>
                    </ul>
                </Col>
            </Row>

        </Collapse>


        <Collapse isOpen={selectedOptionalTaxonomyType.value == 'AFR' && selectedTemplateType.value == 'REPORT'}>
            <Row className="mt-4">
                <Col md="2">
                    <Label>Afr Year:</Label>
                </Col>
                <Col md="2">
                    <Select
                        options={reportYearOptionsTwo}
                        value={selectedAfrYearTwo}
                        onChange={(e: { value: string, label: string }) => { handleAfrYearSelectionChangeTwo(e); }}

                    />
                </Col>
            </Row>
            <Row className="mt-4">
                <Col md="2">
                    <Label>Entity Type:</Label>
                </Col>
                <Col md="10">
                    <ul style={{ listStyle: "none" }}>
                        <Row>
                            {entityCollectionTemplateTypes.map(({ value, label }) => (
                                <Col md="2" hidden={value == 'UNKNOWN'} className="m-0 p-0" >
                                    <li key={value}  >
                                        <Label>
                                            <Input
                                                type="radio"
                                                value={value}
                                                name={label}
                                                onChange={(e: any) => { handleEntityTypeSelectionChangeTwo(e.target) }}

                                                checked={selectedEntityTypeTwo.value == value} />

                                            {label}
                                        </Label>
                                    </li>
                                </Col>
                            ))}
                        </Row>
                    </ul>
                </Col>
            </Row>

        </Collapse>


        <Collapse isOpen={selectedTaxonomyType.value == 'COURT_REPORTING_939' && selectedTemplateType.value == 'DATA_COLLECTION'}>
            <Row className="mt-4">
                <Col md="2">
                    <Label>Quarter Type:</Label>
                </Col>
                <Col md="10">
                    <ul style={{ listStyle: "none" }}>
                        <Row>
                            {courtReporting939QuarterList.map(({ value, label }) => (
                                <Col md="2" hidden={value == 'UNKNOWN'} className="m-0 p-0" >
                                    <li key={value}  >
                                        <Label>
                                            <Input
                                                type="radio"
                                                value={value}
                                                name={label}
                                                onChange={(e: any) => { handle939QuarterTypeSelectionChange(e.target) }}

                                                checked={isChecked939QuarterType(value)} />

                                            {label}
                                        </Label>
                                    </li>
                                </Col>
                            ))}
                        </Row>
                    </ul>
                </Col>
            </Row>

            <Row className="mt-4">
                <Col md="2">
                    <Label>Version:</Label>
                </Col>
                <Col md="10">
                    <Input type="text" value={versioning}
                        onChange={(e) => { handleVersioningChange(e.currentTarget.value) }}
                        

                    />
                </Col>
            </Row>

        </Collapse>

        <Collapse isOpen={selectedOptionalTaxonomyType.value == 'N/A'} >
            <Row className="mt-4">
                <Col md="2">
                    <Label>Report Name:</Label>
                </Col>
                <Col md="10">
                    <Input type="text" value={reportName}
                        onChange={(e) => { handleReportNameChange(e.currentTarget.value) }}

                    />
                </Col>
            </Row>
        </Collapse>

        <Collapse isOpen={selectedTaxonomyType.value == 'COURT_REPORTING_29' && selectedTemplateType.value == 'DATA_COLLECTION'}>           
            <Row className="mt-4">
                <Col md="2">
                    <Label>Version:</Label>
                </Col>
                <Col md="10">
                    <Input type="text" value={versioning}
                        onChange={(e) => { handleVersioningChange(e.currentTarget.value) }}


                    />
                </Col>
            </Row>
        </Collapse>

        <Collapse isOpen={selectedTemplateType.value == 'REPORT' && (allReportAfrInputsSelected() || allReportCourtReportingInputsSelected() || reportName != "" )}>
            <Row className="mt-4">
                <Col md="2">
                    <Label>Output Type:</Label>
                </Col>
                <Col md="10">
                    <ul style={{ listStyle: "none" }}>
                        <Row>
                            {inlineViewerTypeList.map(({ value, label }) => (
                                <Col md="2" hidden={value == 'UNKNOWN'} className="m-0 p-0" >
                                    <li key={value}  >
                                        <Label>
                                            <Input
                                                type="radio"
                                                value={value}
                                                name={label}
                                                onChange={(e: any) => { handleInlineViewerChange(e.target) }}

                                                checked={isCheckedInlineViewerType(value)} />

                                            {label}
                                        </Label>
                                    </li>
                                </Col>
                            ))}
                        </Row>
                    </ul>
                </Col>
            </Row>    
        </Collapse>


        <Collapse isOpen={isMinimumViableSelected() == true}>
            
            <Row className="mt-4">
                <Col>
                    <GenericFileUploader
                        actionType={""}
                        clickAction={handleNewFileUpload}
                        entityType={""}
                        entityId={""}
                        uploadLegend={fileDescription}
                        icon="bi bi-arrow-bar-up"
                        isEnabled={true}
                        uploadedFile={file}
                        fileExtensions={''} />

                </Col>
            </Row>
        </Collapse>
        

    </React.Fragment>)

    const updateRender = (<React.Fragment>
        <Row>
            <Col>
                <h2>Update</h2>
            </Col>
        </Row>
        <Row className="mt-3">
            <Col>
                <Label>Update or delete existing templates.</Label>
            </Col>
        </Row>


        <Row className="mt-4" hidden={componentIsLoading == false}>
            <Col>
                <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                    type="grow" color="warning"
                />
            </Col>
        </Row>

        <div hidden={componentIsLoading == true}>
            <Row className="mt-4">
                <Col>
                    <Select
                        options={templateFileList}
                        value={selectedUpdateFileName}
                        onChange={(e: { value: string, label: string }) => { setSelectedUpdateFileName(e); }}
                    />
                </Col>
            </Row>

            <Row className="mt-4">
                <Col>


                    <GenericActionTile
                        actionType="DOWNLOAD_TEMPLATE"
                        clickAction={handleTemplateDownload}
                        entityName={""}
                        entityCode={""}
                        afrYear={0}
                        tileTitle="Download Template"
                        tileDescription="Download Template"
                        icon="bi bi-download"
                        isEnabled={selectedUpdateFileName.value != 'UNKNOWN'}
                        isLoading={false}
                    />
                </Col>
                <Col>
                    <GenericFileUploader
                        actionType={""}
                        clickAction={handleUpdateFileUpload}
                        entityType={""}
                        entityId={""}
                        uploadLegend={"Update Template"}
                        icon="bi bi-arrow-bar-up"
                        isEnabled={true}
                        uploadedFile={file}
                        fileExtensions={''} />

                </Col>
                <Col>
                    <div className="my-3">
                        <Button className="mr-3 btn btn-danger"
                            disabled={selectedUpdateFileName.value == 'UNKNOWN'}
                            onClick={(e) => { handleDeleteSelectedTemplate() }}
                        >
                            <FontAwesomeIcon icon={faTrashCan} />
                        </Button>
                        Delete Template
                    </div>
                </Col>
            </Row>

        </div>

       
    </React.Fragment>)

    const browseRender = (<React.Fragment>
        <Label>Browse</Label>
    </React.Fragment>)


    const migrateRender = (<React.Fragment>

        <Row>
            <Col>
                <h2>Migrate</h2>
            </Col>
        </Row>
        <Row className="mt-3">
            <Col>
                <Label>Move templates to the database.</Label>
            </Col>
        </Row>
        <Row style={{ overflow: "scroll", height: "40em" }} className="mt-4">
            <Col md="5">
                <Row>
                    <Col>
                        <Label>Existing Templates</Label>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col>
                        <ListGroup>
                            {workingTemplateList.map((option: fileData, idx) => (
                                <ListGroupItem onClick={() => { MoveToAdjustingList(option) }}>{getTemplateDisplayName(option)} {addBadgeForBackedTemplates(option) }</ListGroupItem>
                            ))}
                        </ListGroup>
                    </Col>
                </Row>
            </Col>
            <Col md="2" className=" justify-content-center">
                <Row>
                    <Col>
                        <Button onClick={() => { MoveAllToSameSide() }}>
                            <FontAwesomeIcon
                                style={{}} icon={faRepeat}
                            />
                        </Button>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col>
                        <br />
                        <Button type="button" onClick={(e) => handleRestoreClick()}
                            hidden={adjustingTemplateList.length <= 0}
                            className="taxedit"
                        >
                            Restore
                        </Button>

                    </Col>
                </Row>
            </Col>
            <Col md="5">
                <Row>
                    <Col>
                        <Label>Templates To Store</Label>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col>
                        <ListGroup>
                            {adjustingTemplateList.map((option: fileData, idx) => (
                                <ListGroupItem onClick={() => { MoveToWorkingList(option); }}>{getTemplateDisplayName(option)}</ListGroupItem>
                            ))}
                        </ListGroup>
                    </Col>
                </Row>
            </Col>
        </Row>

    </React.Fragment>);



    return (
        <React.Fragment>

            <Row className="w-100 h-100 m-1">
                <Col md="2" className="mt-1">
                    <ListGroup>                       
                        <ListGroupItem className="border-top-1 border-bottom-1 border-left-0 border-right-0" color={renderMode == 'Create' ? "info" : ""} onClick={() => { setRenderMode('Create') }}>Create</ListGroupItem>
                        <ListGroupItem className="border-top-1 border-bottom-1 border-left-0 border-right-0" color={renderMode == 'Update' ? "info" : "" } onClick={() => { setRenderMode('Update') }}>Update</ListGroupItem>
                        {/*<ListGroupItem className="border-top-1 border-bottom-1 border-left-0 border-right-0" color={renderMode == 'Browse' ? "info" : ""} onClick={() => { setRenderMode('Browse') }}>Browse</ListGroupItem>*/}
                        <ListGroupItem className="border-top-1 border-bottom-1 border-left-0 border-right-0" color={renderMode == 'Migrate' ? "info" : ""} onClick={() => { setRenderMode('Migrate') }}>Migrate</ListGroupItem>
                    </ListGroup>
                </Col>
                <Col md="10">
                    <Collapse isOpen={renderMode == 'Create'}>
                        {createRender}
                    </Collapse>
                    <Collapse isOpen={renderMode == 'Update'}>
                        {updateRender}
                    </Collapse>
                    <Collapse isOpen={renderMode == 'Browse'}>
                        {browseRender}
                    </Collapse>
                    <Collapse isOpen={ renderMode == 'Migrate'}>
                        { migrateRender }
                    </Collapse>
                </Col>
            </Row>


        </React.Fragment>
    );

}

export default TemplateMaintenance;















